import Skeleton from "antd/lib/skeleton";
import Divider from "antd/lib/divider";
import Button from "antd/lib/button";
import Modal from "antd/lib/modal";
import Spin from "antd/lib/spin";
import Table from "antd/lib/table";
import message from "antd/lib/message";
import Tooltip from "antd/lib/tooltip";
import moment from "moment";
import React, { useContext, useState } from "react";
import { useParams } from "react-router";
import { createApiClient } from "apiClient";
import { AppCard } from "components/card";
import {
  CallRecordDesctiptionContainer,
  CallRecordItemGridContainer,
  CallRecordItemHeaderContainer,
  CallRecordItemHeaderText,
  CallRecordProviderNameContainer,
  EmptyDataText,
  ErrorMessage,
  OverflowCallRecordCardContainer
} from "./style";
import { Colors } from "helpers/colors";
import { CallTypes } from "./callRecords";
import {
  EditOutlined,
  InfoCircleOutlined,
  CopyOutlined
} from "@ant-design/icons";
import { AppModal } from "components/modal";
import {
  MeetingContextState,
  MeetingModalContext,
  sessionStatusValues
} from "contextApis/meetingModalContext";
import { UserContext, UserContextState } from "contextApis/userContext";

export interface CallRecord {
  readonly id: number;
  readonly desc: string;
  readonly notes: string;
  readonly call_duration: number;
  readonly prv_name: string;
  readonly date_p: string;
  readonly call_type: string;
  readonly status: string;
  readonly provider_internal_id: number | string;
  readonly is_edited?: boolean;
  readonly last_edited_by_name?: string;
  readonly last_edited_at?: string | null;
}

interface CallLogHistory {
  patient_id: number;
  call_id: number;
  edited_at: string;
  edited_by_name: number;
  notes: string;
}

interface CallRecordListProps {
  readonly data: CallRecord[] | null;
  readonly isLoading: boolean;
  readonly errorMessage: string;
  setSelectedDraft(
    item:
      | {
          id: number;
          date: string;
          itemsDiscussed: string;
          duration: number;
          caller: string;
          other: string;
          callType: string;
          status?: string;
        }
      | undefined
  ): void;
  setShowAddEditCallRecords(type: string): void;
  readonly selectedDraft: {
    id: number;
    date: string;
    itemsDiscussed: string;
    duration: number;
    caller: string;
    other: string;
    callType: string;
    status?: string;
  } | null;
  setChangedDraft: (changed: boolean) => void;
}

interface ExpandableDescriptionProps {
  readonly text: string;
}

const ExpandableDescription: React.FC<ExpandableDescriptionProps> = ({
  text
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsExpanded(false);
    setIsOverflowed(false);
  }, [text]);

  React.useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    let animationFrameId: number | null = null;

    const checkOverflow = () => {
      if (!isExpanded && el.clientHeight > 0) {
        setIsOverflowed(el.scrollHeight > el.clientHeight + 1);
      }
    };

    checkOverflow();

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        if (animationFrameId) {
          window.cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = window.requestAnimationFrame(() => {
          checkOverflow();
        });
      });
      resizeObserver.observe(el);
    }

    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [text, isExpanded]);

  if (!text || text === "-") {
    return <span>-</span>;
  }

  return (
    <div>
      <div
        ref={textRef}
        style={{
          display: isExpanded ? "block" : "-webkit-box",
          WebkitLineClamp: isExpanded ? undefined : 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          wordBreak: "break-word",
          whiteSpace: "pre-line"
        }}
      >
        {text}
      </div>
      {isOverflowed && (
        <span
          onClick={e => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            color: Colors.DodgerBlue || "#1890ff",
            cursor: "pointer",
            fontWeight: 600,
            marginTop: "6px",
            display: "inline-block",
            userSelect: "none"
          }}
        >
          {isExpanded ? "See Less" : "See More"}
        </span>
      )}
    </div>
  );
};

export const CallRecordList: React.FC<CallRecordListProps> = ({
  data,
  isLoading,
  errorMessage,
  setSelectedDraft,
  setShowAddEditCallRecords,
  selectedDraft,
  setChangedDraft
}) => {
  const sortingScheme = ["ACTIVE DRAFT", "DRAFT", "COMPLETED"];
  const meetingContext = useContext<MeetingContextState>(MeetingModalContext);
  const userContext = useContext<UserContextState>(UserContext);
  const { id: userId } = useParams<{ id?: string }>();

  const userRole = (
    userContext?.userData?.role ||
    sessionStorage.getItem("userRole") ||
    ""
  ).toLowerCase();
  const isNurse = userRole.includes("nurse");

  const loggedInUserId =
    userContext?.userData?.internal_id !== undefined &&
    userContext?.userData?.internal_id !== null
      ? String(userContext.userData.internal_id)
      : String(sessionStorage.getItem("userId") || "");

  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historyList, setHistoryList] = useState<CallLogHistory[]>([]);
  const [historySelectedRecord, setHistorySelectedRecord] =
    useState<CallRecord | null>(null);

  const [isAmendModalVisible, setIsAmendModalVisible] = useState(false);
  const [amendSelectedRecord, setAmendSelectedRecord] =
    useState<CallRecord | null>(null);

  const handleOpenAmendModal = (record: CallRecord) => {
    if (
      isNurse &&
      String(loggedInUserId) !== String(record.provider_internal_id)
    ) {
      return;
    }
    setAmendSelectedRecord(record);
    setIsAmendModalVisible(true);
  };

  const handleConfirmAmend = () => {
    if (amendSelectedRecord) {
      if (
        isNurse &&
        String(loggedInUserId) !==
          String(amendSelectedRecord.provider_internal_id)
      ) {
        setIsAmendModalVisible(false);
        setAmendSelectedRecord(null);
        return;
      }
      if (selectedDraft) {
        setChangedDraft(true);
        meetingContext.setCallRecordId(String(amendSelectedRecord.id));
      } else {
        setSelectedDraft({
          id: amendSelectedRecord.id,
          date: amendSelectedRecord.date_p,
          itemsDiscussed: amendSelectedRecord.desc,
          duration: amendSelectedRecord.call_duration,
          caller: amendSelectedRecord.prv_name,
          other: amendSelectedRecord.notes,
          status: amendSelectedRecord.status,
          callType: amendSelectedRecord.call_type
        });
      }
      setShowAddEditCallRecords("edit");
    }
    setIsAmendModalVisible(false);
    setAmendSelectedRecord(null);
  };

  const handleCancelAmend = () => {
    setIsAmendModalVisible(false);
    setAmendSelectedRecord(null);
  };

  const handleOpenHistory = async (record: CallRecord) => {
    setHistorySelectedRecord(record);
    setIsHistoryModalVisible(true);
    setIsLoadingHistory(true);
    try {
      const patientId = userId || "";

      const result: any = await createApiClient().getCallLogHistory(
        patientId,
        record.id
      );

      setHistoryList(result?.data || []);
    } catch (err) {
      console.log("getCallLogHistory error", err);
      message.error("Failed to fetch call log history");
      setHistoryList([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  function compare(a: any, b: any) {
    const indexOfa = sortingScheme.indexOf(a.status);
    const indexOfb = sortingScheme.indexOf(b.status);

    if (indexOfa > indexOfb) {
      return 1;
    }
    if (indexOfa < indexOfb) {
      return -1;
    }
    return 0;
  }

  const checkActiveDraft = (
    call_duration: number,
    status: string,
    call_type: string
  ) => {
    if (
      !call_duration &&
      status === "DRAFT" &&
      call_type !== CallTypes.MANUAL
    ) {
      return true;
    }
    return false;
  };

  const handleCopyTextContent = async (record: CallRecord) => {
    const notesText = record.notes || "";
    const htmlText = `<pre style="font-family:sans-serif;font-size:14px;color:#333;margin:0;white-space:pre-wrap;word-wrap:break-word;">${notesText}</pre>`;

    try {
      if (typeof (window as any).ClipboardItem !== "undefined") {
        const blobHtml = new Blob([htmlText], { type: "text/html" });
        const blobText = new Blob([notesText], { type: "text/plain" });
        const data = [
          new (window as any).ClipboardItem({
            "text/html": blobHtml,
            "text/plain": blobText
          })
        ];
        await navigator.clipboard.write(data);
        message.success("Copied to clipboard");
      } else {
        await navigator.clipboard.writeText(notesText);
        message.success("Copied to clipboard");
      }
    } catch (err) {
      navigator.clipboard
        .writeText(notesText)
        .then(() => {
          message.success("Copied to clipboard");
        })
        .catch(() => {
          message.error("Failed to copy");
        });
    }
  };

  return (
    <AppCard cardHeight="100%" cardWidth="100%">
      <OverflowCallRecordCardContainer>
        {isLoading ? (
          <>
            <Skeleton loading={isLoading} active={isLoading} />
            <Divider />
            <Skeleton loading={isLoading} active={isLoading} />
          </>
        ) : errorMessage ? (
          <ErrorMessage>{errorMessage}</ErrorMessage>
        ) : data && data.length <= 0 ? (
          <EmptyDataText>No Data</EmptyDataText>
        ) : (
          data?.sort(compare).map((record, index) => {
            const isOwner =
              loggedInUserId !== "" &&
              String(loggedInUserId) === String(record.provider_internal_id);

            const canNurseEdit = !isNurse || isOwner;

            const selectableForUser =
              (!selectedDraft || selectedDraft.id !== record.id) &&
              canNurseEdit;

            const isDraft =
              checkActiveDraft(
                record.call_duration,
                record.status,
                record.call_type
              ) || record.status?.toLowerCase().replace(/\s/g, "") === "draft";

            const minutesValue = record.call_duration / 60;
            const fullMinutes = Math.floor(minutesValue);
            const seconds = Math.round((minutesValue - fullMinutes) * 60);
            const formattedSeconds = seconds.toString().padStart(2, "0");
            const formattedDuration =
              seconds > 0
                ? `${fullMinutes}:${formattedSeconds}`
                : `${fullMinutes}`;

            return (
              <div
                key={index}
                data-testid="call-record-list-item"
                // onClick={() => {
                //   //If selected draft is already selected do not allow second click
                //   if (selectableForUser) {
                //     if (
                //       meetingContext.sessionStatus ===
                //       sessionStatusValues.STARTED
                //     ) {
                //       if (
                //         checkActiveDraft(
                //           record.call_duration,
                //           record.status,
                //           record.call_type
                //         )
                //       ) {
                //         if (selectedDraft) {
                //           meetingContext.setCallRecordId(String(record.id));
                //         } else {
                //           setSelectedDraft({
                //             id: record.id,
                //             date: record.date_p,
                //             itemsDiscussed: record.desc,
                //             duration: record.call_duration,
                //             caller: record.prv_name,
                //             other: record.notes,
                //             status: record.status,
                //             callType: record.call_type
                //           });
                //         }
                //         setShowAddEditCallRecords("edit");
                //       }
                //     } else {
                //       //If selected draft exists, set changedDraft to enable auto save in EditCallRecord
                //       //set callRecordId which will allow setSelectedDraft from getCallRecords call in callRecords.tsx onSuccess
                //       if (selectedDraft) {
                //         setChangedDraft(true);
                //         meetingContext.setCallRecordId(String(record.id));
                //       } else {
                //         setSelectedDraft({
                //           id: record.id,
                //           date: record.date_p,
                //           itemsDiscussed: record.desc,
                //           duration: record.call_duration,
                //           caller: record.prv_name,
                //           other: record.notes,
                //           status: record.status,
                //           callType: record.call_type
                //         });
                //       }
                //       setShowAddEditCallRecords("edit");
                //     }
                //   }
                // }}
                style={
                  selectedDraft?.id === record.id
                    ? {
                        background: Colors.BabyBlue,
                        borderRadius: "5px",
                        padding: "5px 5px 0px 5px"
                      }
                    : record.status === "COMPLETED"
                    ? {
                        background: "#E1E1E1",
                        borderRadius: "5px",
                        padding: "5px 5px 0px 5px"
                      }
                    : {
                        background: Colors.White,
                        borderRadius: "5px",
                        padding: "5px 5px 0px 5px"
                      }
                }
                // style={
                //   selectedDraft?.id === record.id
                //     ? {
                //         background: Colors.BabyBlue,
                //         borderRadius: "5px",
                //         padding: "5px 5px 0px 5px"
                //       }
                //     : (meetingContext.sessionStatus ===
                //         sessionStatusValues.STARTED &&
                //         !checkActiveDraft(
                //           record.call_duration,
                //           record.status,
                //           record.call_type
                //         )) ||
                //       !selectableForUser ||
                //       record.status == "COMPLETED"
                //     ? {
                //         background: "#E1E1E1",
                //         borderRadius: "5px",
                //         padding: "5px 5px 0px 5px"
                //       }
                //     : {}
                // }
              >
                <CallRecordItemHeaderContainer>
                  <CallRecordItemHeaderText>
                    {record.date_p}
                    {record.is_edited && (
                      <>
                        <span
                          style={{
                            color: Colors.Grey,
                            marginLeft: "5px",
                            fontStyle: "italic"
                          }}
                        >
                          Edited
                        </span>{" "}
                      </>
                    )}
                  </CallRecordItemHeaderText>
                  <CallRecordItemHeaderText
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    <Tooltip title="Copy text content">
                      <CopyOutlined
                        className="exclude-from-copy"
                        style={{ cursor: "pointer", color: Colors.Grey }}
                        onClick={e => {
                          e.stopPropagation();
                          handleCopyTextContent(record);
                        }}
                      />
                    </Tooltip>
                    <span>
                      {/* <span style={{ color: Colors.Mantis }}>
                        {editableForUser && "EDITABLE"}
                      </span>{" "} */}
                      <span style={{ color: Colors.DodgerBlue }}>
                        {checkActiveDraft(
                          record.call_duration,
                          record.status,
                          record.call_type
                        ) ? (
                          <span style={{ color: Colors.Mantis }}>
                            ACTIVE DRAFT
                          </span>
                        ) : record.status.toLowerCase().replace(/\s/g, "") ===
                          "draft" ? (
                          "DRAFT"
                        ) : (
                          ""
                        )}
                      </span>{" "}
                      {formattedDuration} min
                    </span>
                  </CallRecordItemHeaderText>
                </CallRecordItemHeaderContainer>
                <CallRecordItemGridContainer>
                  <div
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                      Items Discussed:
                    </div>
                    {record.desc.split(/<br>/).map((item, index) => (
                      <div key={index}>{item}</div>
                    ))}
                  </div>
                  <CallRecordDesctiptionContainer
                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                  >
                    {record.notes}
                  </CallRecordDesctiptionContainer>
                </CallRecordItemGridContainer>
                <CallRecordProviderNameContainer
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "4px"
                  }}
                >
                  {record.is_edited &&
                    (record.last_edited_at ? (
                      <Tooltip
                        title={`Modified on : ${
                          moment(record.last_edited_at).isValid()
                            ? moment(record.last_edited_at).format(
                                "MMMM DD, YYYY hh:mm A"
                              )
                            : record.last_edited_at
                        }`}
                      >
                        <div style={{ cursor: "pointer" }}>
                          <span style={{ fontStyle: "italic" }}>
                            Modified by :{" "}
                          </span>
                          {record.last_edited_by_name}
                        </div>
                      </Tooltip>
                    ) : (
                      <div>
                        <span style={{ fontStyle: "italic" }}>
                          Modified by :{" "}
                        </span>
                        {record.last_edited_by_name}
                      </div>
                    ))}
                  <div>{record.prv_name}</div>
                  <div
                    className="exclude-from-copy"
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center"
                    }}
                  >
                    <Button
                      size="small"
                      danger
                      disabled={!canNurseEdit}
                      style={{
                        fontSize: "11px",
                        height: "22px",
                        padding: "0 8px",
                        color: !canNurseEdit ? Colors.Grey : Colors.Red,
                        borderColor: !canNurseEdit ? Colors.Grey : Colors.Red,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px"
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        if (!canNurseEdit) return;
                        handleOpenAmendModal(record);
                      }}
                    >
                      <EditOutlined
                        style={{
                          color: !canNurseEdit ? Colors.Grey : Colors.Red
                        }}
                      />
                    </Button>

                    <Button
                      size="small"
                      type="primary"
                      ghost
                      disabled={!record.is_edited}
                      style={{
                        fontSize: "11px",
                        height: "22px",
                        padding: "0 8px",
                        backgroundColor: Colors.White
                      }}
                      onClick={e => {
                        e.stopPropagation();
                        handleOpenHistory(record);
                      }}
                    >
                      Call Log History
                    </Button>
                  </div>
                </CallRecordProviderNameContainer>
                {index < data.length - 1 && <Divider />}
              </div>
            );
          })
        )}
      </OverflowCallRecordCardContainer>
      <Modal
        title={"Call Log History"}
        visible={isHistoryModalVisible}
        onCancel={() => setIsHistoryModalVisible(false)}
        footer={null}
        width={750}
        centered
      >
        {isLoadingHistory ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : historyList && historyList.length > 0 ? (
          <Table
            dataSource={historyList.map((item, idx) => ({
              key: idx,
              ...item
            }))}
            columns={[
              {
                title: "Edited By",
                dataIndex: "edited_by_name",
                key: "edited_by_name",
                render: (value: number) => value || "-"
              },
              {
                title: "Edited At",
                dataIndex: "edited_at",
                key: "edited_at",
                render: (value: string) =>
                  value ? moment(value).format("MMMM DD, YYYY hh:mm A") : "-"
              },
              {
                title: "Description",
                dataIndex: "notes",
                key: "notes",
                render: (notes: string) => {
                  let displayContent = "-";
                  try {
                    const parsedNotes = JSON.parse(notes);

                    if (Array.isArray(parsedNotes)) {
                      const descList = parsedNotes
                        .filter(
                          (item: any) =>
                            item?.desc &&
                            item?.isSelected !== false &&
                            item?.isSelected !== "false"
                        )
                        .map((item: any) => item.desc.trim())
                        .filter(Boolean);

                      displayContent =
                        descList.length > 0 ? descList.join(", ") : "-";
                    } else {
                      displayContent = parsedNotes || notes || "-";
                    }
                  } catch {
                    displayContent = notes || "-";
                  }

                  return (
                    <ExpandableDescription text={String(displayContent)} />
                  );
                }
              }
            ]}
            pagination={{
              pageSize: 5
            }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "30px" }}>
            No call log history records found.
          </div>
        )}
      </Modal>
      <AppModal
        visible={isAmendModalVisible}
        title="Confirmation"
        cancelText="No"
        okText="Yes"
        onCancel={handleCancelAmend}
        onOk={handleConfirmAmend}
        prompt={{
          icon: (
            <InfoCircleOutlined
              style={{ color: "#F5A623", fontSize: "40px" }}
            />
          ),
          text: (
            <div>
              Amend this record{" "}
              <span style={{ fontFamily: "Arial, sans-serif" }}>?</span>
            </div>
          )
        }}
        footer
      />
    </AppCard>
  );
};
